// Pinned to common-contracts/static_page_rich_text.json (see staticPageRichText.test.ts).
export const STATIC_PAGE_RICH_TEXT = {
  version: 1,
  blockKinds: ['paragraph', 'heading', 'list', 'quote', 'code', 'hr'],
  headingLevels: [2, 3],
  listStyles: ['bullet', 'ordered'],
  maxListDepth: 3,
  aligns: ['left', 'center', 'right'],
  marks: ['bold', 'italic', 'code'],
  sizes: ['xs', 's', 'm', 'l', 'xl', '2xl', '3xl'],
  defaultSize: 'm',
  colorPattern: '^[0-9A-Fa-f]{6}$',
  linkPattern: '^(https?://|mailto:|/)',
  maxLinkLength: 2000,
  maxBlocksPerDoc: 200,
  maxRunsPerBlock: 200,
  columnMaxTextLength: 20000,
  captionMaxTextLength: 2000,
} as const satisfies {
  version: 1;
  blockKinds: readonly string[];
  headingLevels: readonly number[];
  listStyles: readonly string[];
  maxListDepth: number;
  aligns: readonly RichAlign[];
  marks: readonly RichMark[];
  sizes: readonly RichSize[];
  defaultSize: RichSize;
  colorPattern: string;
  linkPattern: string;
  maxLinkLength: number;
  maxBlocksPerDoc: number;
  maxRunsPerBlock: number;
  columnMaxTextLength: number;
  captionMaxTextLength: number;
};

export type RichAlign = 'left' | 'center' | 'right';
export type RichMark = 'bold' | 'italic' | 'code';
export type RichSize = 'xs' | 's' | 'm' | 'l' | 'xl' | '2xl' | '3xl';

export interface TextRun {
  text: string;
  marks?: RichMark[];
  size?: RichSize;
  color?: string; // 6-hex, no leading '#'
  link?: string;
}

export interface ImageRun {
  image: { asset_ref: string; alt?: string };
}

export type RichRun = TextRun | ImageRun;

export interface RichListItem {
  runs: RichRun[];
  items?: RichListItem[];
}

export type RichBlockNode =
  | { kind: 'paragraph'; align?: RichAlign; runs: RichRun[] }
  | { kind: 'heading'; level: 2 | 3; align?: RichAlign; runs: RichRun[] }
  | { kind: 'list'; style: 'bullet' | 'ordered'; items: RichListItem[] }
  | { kind: 'quote'; runs: RichRun[] }
  | { kind: 'code'; text: string }
  | { kind: 'hr' };

/** The structured rich text document; `rt` is the version tag that
 * discriminates a doc from a legacy markdown string column. */
export interface RichDoc {
  rt: 1;
  blocks: RichBlockNode[];
}

export function isTextRun(run: RichRun): run is TextRun {
  return typeof (run as TextRun).text === 'string';
}

export function isRichDoc(value: unknown): value is RichDoc {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const doc = value as { rt?: unknown; blocks?: unknown };
  return doc.rt === 1 && Array.isArray(doc.blocks);
}

export function emptyRichDoc(): RichDoc {
  return { rt: 1, blocks: [] };
}

/** True when the doc carries no content: no blocks, or only paragraphs whose
 * text runs are all blank (an image run counts as content). */
export function isEmptyRichDoc(doc: RichDoc): boolean {
  return doc.blocks.every(
    (block) =>
      block.kind === 'paragraph' &&
      block.runs.every((run) => isTextRun(run) && run.text.trim() === ''),
  );
}
